import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, Settings, Wrench, Download, Info, Moon, Sun,
  Save, RotateCcw, CheckCircle, Upload, Trash2, FileText, BookOpen
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { controllerConfig } from '@/utils/gcode';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  
  const [settings, setSettings] = useState({
    defaultController: 'fanuc',
    defaultMachineType: 'turning',
    defaultUnits: 'imperial',
    showLineNumbers: true,
    defaultCoolant: true,
    defaultSpindleLimit: 3500,
    defaultSafeZ: 1.0,
    defaultSafeX: 4.0,
  });
  
  const [saved, setSaved] = useState(false);
  const [trainingDocs, setTrainingDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadMsg, setUploadMsg] = useState(null);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/training-docs`);
      setTrainingDocs(res.data);
    } catch { setTrainingDocs([]); }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('title', uploadTitle || file.name);
      await axios.post(`${API}/training-docs`, form);
      setUploadTitle('');
      setUploadMsg({ type: 'success', text: 'Document uploaded successfully' });
      fetchDocs();
    } catch (err) {
      setUploadMsg({ type: 'error', text: err.response?.data?.detail || 'Upload failed' });
    }
    setUploading(false);
    e.target.value = '';
    setTimeout(() => setUploadMsg(null), 4000);
  };

  const handleDeleteDoc = async (id) => {
    try {
      await axios.delete(`${API}/training-docs/${id}`);
      setTrainingDocs(trainingDocs.filter(d => d.id !== id));
    } catch (err) { console.error(err); }
  };
  
  const handleSave = () => {
    localStorage.setItem('gcodeProSettings', JSON.stringify(settings));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  
  const handleReset = () => {
    setSettings({
      defaultController: 'fanuc',
      defaultMachineType: 'turning',
      defaultUnits: 'imperial',
      showLineNumbers: true,
      defaultCoolant: true,
      defaultSpindleLimit: 3500,
      defaultSafeZ: 1.0,
      defaultSafeX: 4.0,
    });
  };

  return (
    <div className="min-h-screen bg-industrial-bg pb-20">
      {/* Sub Header */}
      <div className="sticky top-14 z-20 bg-industrial-surface border-b border-industrial-border px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-industrial-text-secondary hover:text-industrial-text transition-colors"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </button>
          
          <h2 className="font-heading font-bold text-industrial-text flex items-center gap-2">
            <Settings className="w-5 h-5" />
            SETTINGS
          </h2>

          <div className="w-16" />
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4 animate-in">
        {/* Theme Toggle */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {theme === 'dark' ? (
                <Moon className="w-5 h-5 text-industrial-readout" />
              ) : (
                <Sun className="w-5 h-5 text-industrial-warning" />
              )}
              <div>
                <h3 className="font-medium text-industrial-text">Theme</h3>
                <p className="text-xs text-industrial-text-secondary">
                  {theme === 'dark' ? 'Dark mode enabled' : 'Light mode enabled'}
                </p>
              </div>
            </div>
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className={`w-14 h-7 rounded-full transition-colors relative ${
                theme === 'dark' ? 'bg-industrial-readout' : 'bg-industrial-warning'
              }`}
            >
              <span 
                className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-all ${
                  theme === 'dark' ? 'left-8' : 'left-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Default Controller */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
          <h3 className="font-heading font-semibold text-industrial-text mb-3 flex items-center gap-2">
            <Wrench className="w-4 h-4 text-industrial-readout" />
            Default Controller
          </h3>
          <select
            value={settings.defaultController}
            onChange={(e) => setSettings({ ...settings, defaultController: e.target.value })}
            className="w-full bg-industrial-bg border border-industrial-border rounded-lg px-4 py-3 text-industrial-text"
            data-testid="controller-select"
          >
            {Object.entries(controllerConfig).map(([key, config]) => (
              <option key={key} value={key}>{config.name}</option>
            ))}
          </select>
          <p className="text-xs text-industrial-text-secondary mt-2">
            Controllers available: FANUC, Siemens SINUMERIK, Haas, Heidenhain, Okuma OSP, Fadal, Mazak, Doosan
          </p>
        </div>

        {/* Default Machine Type */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
          <h3 className="font-heading font-semibold text-industrial-text mb-3">
            Default Machine Type
          </h3>
          <div className="grid grid-cols-3 gap-2">
            {['turning', 'milling', 'turn-mill'].map(type => (
              <button
                key={type}
                onClick={() => setSettings({ ...settings, defaultMachineType: type })}
                data-testid={`machine-${type}`}
                className={`p-3 rounded-lg border text-center text-sm transition-all ${
                  settings.defaultMachineType === type
                    ? 'bg-industrial-success/20 border-industrial-success text-industrial-success'
                    : 'bg-industrial-bg border-industrial-border text-industrial-text-secondary hover:border-industrial-success'
                }`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* G-Code Defaults */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
          <h3 className="font-heading font-semibold text-industrial-text mb-3">
            G-Code Defaults
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-industrial-text-secondary mb-1 block">
                  Max Spindle RPM
                </label>
                <input
                  type="number"
                  value={settings.defaultSpindleLimit}
                  onChange={(e) => setSettings({ ...settings, defaultSpindleLimit: parseInt(e.target.value) || 3500 })}
                  className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                />
              </div>
              <div>
                <label className="text-xs text-industrial-text-secondary mb-1 block">
                  Safe Z Retract (in)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={settings.defaultSafeZ}
                  onChange={(e) => setSettings({ ...settings, defaultSafeZ: parseFloat(e.target.value) || 1.0 })}
                  className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <span className="text-industrial-text">Show Line Numbers</span>
                <p className="text-xs text-industrial-text-secondary">Display N### in output</p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, showLineNumbers: !settings.showLineNumbers })}
                className={`w-14 h-7 rounded-full transition-colors relative ${
                  settings.showLineNumbers ? 'bg-industrial-success' : 'bg-industrial-border'
                }`}
              >
                <span className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-all ${
                  settings.showLineNumbers ? 'left-8' : 'left-1'
                }`} />
              </button>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <span className="text-industrial-text">Default Coolant On</span>
                <p className="text-xs text-industrial-text-secondary">M08 enabled by default</p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, defaultCoolant: !settings.defaultCoolant })}
                className={`w-14 h-7 rounded-full transition-colors relative ${
                  settings.defaultCoolant ? 'bg-industrial-success' : 'bg-industrial-border'
                }`}
              >
                <span className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-all ${
                  settings.defaultCoolant ? 'left-8' : 'left-1'
                }`} />
              </button>
            </div>
          </div>
        </div>

        {/* Save/Reset Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleReset}
            className="flex-1 py-3 border border-industrial-border text-industrial-text rounded-lg flex items-center justify-center gap-2 hover:bg-industrial-surface-highlight transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset Defaults
          </button>
          <button
            onClick={handleSave}
            data-testid="save-settings"
            className="flex-1 py-3 bg-industrial-success text-black font-bold rounded-lg flex items-center justify-center gap-2"
          >
            {saved ? (
              <>
                <CheckCircle className="w-4 h-4" />
                Saved!
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Settings
              </>
            )}
          </button>
        </div>

        {/* Training Documents */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-5 h-5 text-industrial-safety" />
            <h3 className="font-heading font-semibold text-industrial-text">AI Training Documents</h3>
          </div>
          <p className="text-xs text-industrial-text-secondary mb-3">
            Upload machining docs (PDF, TXT, NC files) to expand the AI knowledge base. These are injected into the AI prompt when analyzing blueprints.
          </p>

          {/* Upload */}
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              placeholder="Document title (optional)"
              className="flex-1 bg-industrial-bg border border-industrial-border rounded-lg px-3 py-2 text-sm text-industrial-text placeholder:text-industrial-text-secondary"
              data-testid="training-doc-title"
            />
            <label className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 cursor-pointer transition-colors ${uploading ? 'bg-industrial-surface-highlight text-industrial-text-secondary' : 'bg-industrial-safety text-black hover:bg-industrial-safety/90'}`}>
              <Upload className="w-4 h-4" />
              {uploading ? 'Uploading...' : 'Upload'}
              <input
                type="file"
                accept=".pdf,.txt,.nc,.ngc,.gcode,.tap,.cnc,.doc,.docx"
                onChange={handleUploadDoc}
                className="hidden"
                disabled={uploading}
                data-testid="training-doc-file"
              />
            </label>
          </div>

          {/* Upload Feedback */}
          {uploadMsg && (
            <div className={`text-xs px-3 py-2 rounded-lg mb-3 ${uploadMsg.type === 'success' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`} data-testid="upload-feedback">
              {uploadMsg.text}
            </div>
          )}

          {/* Doc List */}
          {trainingDocs.length === 0 ? (
            <p className="text-xs text-industrial-text-secondary text-center py-3">No training documents uploaded yet</p>
          ) : (
            <div className="space-y-2">
              {trainingDocs.map(doc => (
                <div key={doc.id} className="flex items-center gap-2 bg-industrial-bg border border-industrial-border rounded-lg px-3 py-2" data-testid={`training-doc-${doc.id}`}>
                  <FileText className="w-4 h-4 text-industrial-safety flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-industrial-text truncate">{doc.title}</div>
                    <div className="text-[10px] text-industrial-text-secondary">{doc.filename} — {(doc.char_count / 1000).toFixed(1)}KB</div>
                  </div>
                  <button
                    onClick={() => handleDeleteDoc(doc.id)}
                    className="p-1.5 rounded hover:bg-red-500/20"
                    data-testid={`delete-doc-${doc.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-industrial-readout flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-industrial-text mb-1">G-Code Pro</h3>
              <p className="text-sm text-industrial-text-secondary mb-2">
                Professional CNC G-code generator for turning and milling machines.
              </p>
              <p className="text-xs text-industrial-text-secondary">
                Version 1.0.0 | Imperial Units (Inches)
              </p>
              <p className="text-xs text-industrial-text-secondary mt-2">
                Creator/Developer - Thomas Riddick, CNC Machinist
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
